/**
 * Check uploaded file columns against profile expectations.
 */
import * as XLSX from 'xlsx';
import { PROFILES } from '../profiles/index';
import { findISolvedSourceSheet, findKeywordDetailSheet } from './isolved-parser.js';
import { buildPlan } from './ingest';

const COLUMN_ALIASES = {
  unit: ['unit', 'unit_id', 'business_unit', 'business_unit_id', 'bu'],
  account: ['account', 'account_no', 'account_number', 'acct'],
  company_id: ['company_id', 'company', 'companyid'],
  accounting_date: ['accounting_date', 'acct_date', 'date'],
  description: ['description', 'remarks', 'desc'],
  pay_period: ['pay_period', 'period', 'payperiod'],
  project_type: ['project_type', 'projtype', 'proj_type'],
  project_id: ['project_id', 'project', 'proj_id', 'projid'],
  amount: ['amount', 'amt', 'net_amount'],
  debit: ['debit', 'dr'],
  credit: ['credit', 'cr'],
};

function normalizeHeader(key) {
  return String(key).trim().toLowerCase().replace(/\s+/g, '_');
}

function resolveCanonical(header, knownColumns) {
  const norm = normalizeHeader(header);
  if (knownColumns.includes(norm)) return norm;

  for (const [canonical, aliases] of Object.entries(COLUMN_ALIASES)) {
    if (aliases.includes(norm) && knownColumns.includes(canonical)) return canonical;
  }
  return null;
}

/**
 * Read first sheet headers and compare to profile sourceColumns.
 * Returns { found, missing, extra, headers, rowCount }
 */
export async function checkColumns(file, profileKey) {
  const profile = PROFILES[profileKey];
  if (!profile) throw new Error(`Unknown profile: ${profileKey}`);

  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: 'array', cellDates: true });

  const found = new Set();
  const extra = [];
  let rawHeaders = [];
  let rowCount = 0;

  // For payroll, scan all sheets to find iSolved GL Report tabular data
  if (profileKey === 'gl8610xl-payroll') {
    let raw;
    let headerRow;
    // Tabular iSolved export first, then a workbook already in GL8610XL
    // keyword format (Layout + Detail rows) — the payroll transformer accepts
    // both, so the readiness check has to recognise both too.
    const tabular = findISolvedSourceSheet(wb);
    const source = tabular ?? findKeywordDetailSheet(wb);
    if (source) {
      raw = source.rows;
      // Keyword sheets carry the date in a header keyword, not a detail column.
      headerRow = tabular
        ? ['Date', 'Unit', 'Account', 'Amount', 'Desc']
        : ['Unit', 'Account', 'Amount', 'Desc'];
    } else {
      const sheet = wb.Sheets[wb.SheetNames[0]];
      raw = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      headerRow = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' })[0] ?? [];
    }

    rawHeaders = headerRow.map((h) => String(h).trim()).filter(Boolean);
    rowCount = raw.length;

    // Optional columns count as recognised, not as "extra (ignored)" — the
    // transformers do read them (project type/id, company id…).
    const knownColumns = [...profile.sourceColumns, ...(profile.optionalColumns ?? [])];
    rawHeaders.forEach((h) => {
      const canonical = resolveCanonical(h, knownColumns);
      if (canonical) {
        found.add(canonical);
      } else if (normalizeHeader(h)) {
        extra.push(h);
      }
    });
  } else {
    // Report exactly what the ingest analyzer resolved, so the readiness banner
    // can't disagree with the mapping the transform engine actually applies.
    // Reading row 0 blindly flagged valid files as broken (title rows, or the
    // keyword header block above a Layout row), and this file's own alias table
    // resolved fewer headers than ingest's ("Account No.", "JAN-26").
    const plan = buildPlan(wb, profileKey);
    rawHeaders = plan.columns.map((c) => c.sourceHeader);
    rowCount = plan.rowCount;
    plan.columns.forEach((c) => {
      if (c.canonical) found.add(c.canonical);
      else extra.push(c.sourceHeader);
    });
  }

  const missing = profile.sourceColumns.filter((col) => {
    if (profile.requiredFields.includes(col)) return !found.has(col);
    // Payroll: date column is optional if found via alias
    if (profileKey === 'gl8610xl-payroll' && col === 'date') {
      return !found.has('date') && !found.has('accounting_date');
    }
    // Optional columns only flagged if no monthly cols found for budget
    if (profileKey === 'gl9300c-budget') {
      const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
      if (months.includes(col)) {
        const anyMonth = months.some((m) => found.has(m));
        return !anyMonth && col === 'jan';
      }
    }
    return false;
  });

  const requiredMissing = profile.requiredFields.filter((f) => !found.has(f));

  return {
    found: [...found],
    missing,
    requiredMissing,
    extra,
    headers: rawHeaders,
    rowCount,
    isReady: requiredMissing.length === 0 && rowCount > 0,
  };
}
